'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect, useState } from 'react';

/**
 * Returns the height (px) obscured at the bottom of the panel by the mobile
 * browser toolbar and/or the on-screen keyboard, derived from the visual
 * viewport measured against the panel's own rendered height.
 *
 * A fullscreen dialog stays full-screen (covering the page) while reserving
 * this much space at the bottom so pinned controls remain visible. Measuring
 * the panel element directly — rather than window.innerHeight — is essential on
 * iOS Safari, where a `fixed inset-0` panel spans the large viewport (behind the
 * toolbar) but window.innerHeight reports the visible height. Returns 0 when
 * disabled or when the visual viewport API is unavailable.
 */
export function useKeyboardInset(enabled, panelRef) {
  const $ = _c(4);
  const [inset, setInset] = useState(0);
  let t0;
  let t1;
  if ($[0] !== enabled || $[1] !== panelRef) {
    t0 = () => {
      if (!enabled || typeof window === "undefined") {
        setInset(0);
        return;
      }
      const viewport = window.visualViewport;
      if (!viewport) {
        setInset(0);
        return;
      }
      const update = function update() {
        const panel = panelRef.current;
        const vv = window.visualViewport;
        if (!panel || !vv) {
          setInset(0);
          return;
        }
        setInset(Math.max(0, panel.clientHeight - vv.height - vv.offsetTop));
      };
      update();
      viewport.addEventListener("resize", update);
      viewport.addEventListener("scroll", update);
      const observer = new ResizeObserver(update);
      if (panelRef.current) {
        observer.observe(panelRef.current);
      }
      return () => {
        viewport.removeEventListener("resize", update);
        viewport.removeEventListener("scroll", update);
        observer.disconnect();
      };
    };
    t1 = [enabled, panelRef];
    $[0] = enabled;
    $[1] = panelRef;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return inset;
}